// @flow strict

import * as React from 'react'
import cx from 'classnames'
import {
  type PropsWithChildren,
  BusinessButton,
  AccessibilityText,
} from '@mondough/public-web-components'
import styles from '../TextAndImageSection.module.scss'
import sharedStyles from '../deviceVisibility.module.scss'
import { SectionWrapper, SectionThemes } from '../SectionWrapper'
import SubtitleSection from '../SubtitleSection'
import { MarkdownContent } from '../MarkdownContent'

import businessStyles from './BusinessTextAndImage.module.scss'
import { type TextAndImageProps } from '../TextAndImageSection'
import Dialog from '@reach/dialog'

const PlayButton = ({ onClick }) => (
  <div className={businessStyles.playButtonWrapper}>
    <button onClick={onClick} className={businessStyles.playButton}>
      <AccessibilityText>Watch video</AccessibilityText>
      <svg width="18" height="24" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M.379.701l17.677 11.678L.38 24.056z"
          fill="#FFF"
          fillRule="evenodd"
        />
      </svg>
    </button>
  </div>
)

const BusinessTextAndImage = ({
  title,
  description,
  imageUrls,
  imageAlts,
  reverse,
  children,
  cta,
  flags,
}: PropsWithChildren<
  TextAndImageProps,
  React.Element<typeof SubtitleSection>,
>) => {
  const [showVideo, setShowVideo] = React.useState(false)
  const hasVideo =
    flags != null && flags.videoID != null && flags.videoID !== ''

  return (
    <SectionWrapper
      theme={SectionThemes.LIGHT}
      className={businessStyles.sectionWrapper}
      contentClassName={cx(businessStyles.wrapper, {
        [styles.reverseContent]: reverse,
        [styles.forwardContent]: !reverse,
      })}
    >
      {children}
      <div className={cx(styles.textCol, businessStyles.textAndImage)}>
        <div className={styles.textContent}>
          {/*
          
           * Build the title as a <h3 /> when children exist.
           * This is because the child elements will be a <SubtitleSection />
           * which will build the <SubtitleSection /> with an <h2 />
           */
          children ? (
            <h3 className={cx(styles.title, businessStyles.title)}>{title}</h3>
          ) : (
            <h2 className={cx(styles.title, businessStyles.title)}>{title}</h2>
          )}
          <MarkdownContent className={businessStyles.copy}>
            {description}
          </MarkdownContent>
          {cta && (
            <div className={styles.cta}>
              <BusinessButton href={cta.href}>{cta.text}</BusinessButton>
            </div>
          )}
        </div>
      </div>
      <div className={cx(styles.imgCol, businessStyles.imageWrapper)}>
        {hasVideo && <PlayButton onClick={() => setShowVideo(true)} />}

        <img
          src={imageUrls && imageUrls.desktop}
          alt={imageAlts ? imageAlts.desktop : ''}
          className={cx(sharedStyles.desktopOnly, {
            [businessStyles.videoPreview]: hasVideo,
          })}
        />
        <img
          src={imageUrls && (imageUrls.mobile || imageUrls.desktop)}
          alt={imageAlts ? imageAlts.mobile || imageAlts.desktop : ''}
          className={cx(sharedStyles.mobileOnly, {
            [businessStyles.videoPreview]: hasVideo,
          })}
        />
      </div>
      {showVideo && flags && flags.videoID && (
        <Dialog
          className={businessStyles.modal}
          aria-label="Customer testimonial video"
          onDismiss={() => setShowVideo(false)}
        >
          <div className={businessStyles.videoWrapper}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${
                flags.videoID
              }?modestbranding=1&rel=0`}
              frameBorder="0"
              allowFullScreen
              allow="autoplay; encrypted-media; picture-in-picture"
              title="Customer testimonial video"
            />
          </div>
        </Dialog>
      )}
    </SectionWrapper>
  )
}

export default BusinessTextAndImage
